import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIndicators, setMapIndicators, setWorkspaceLoaded, clearWorkspace, clearPersistedWorkspace } from 'store/workspaceSlice';
import { listWorkspaces, getWorkspaceById, refreshToken } from '../../../store/auth';
import { BASE_URL } from '../../../commonConfig/ApiConfig';
import store from '../../../store/store';
import SessionExtensionModal from './SessionExtensionModal';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isWorkspaceLoaded = useSelector(state => state.workspace.isWorkspaceLoaded);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionExtension, setShowSessionExtension] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  // Note: Session timeout is now handled by backend token expiration
  // Access tokens expire in 10 minutes, refresh tokens in 7 days
  // Frontend automatically refreshes tokens when they expire

  const loadDefaultWorkspace = async () => {
    try {
      // Add a small delay to ensure token is properly set in localStorage
      await new Promise(resolve => setTimeout(resolve, 100));
      const response = await listWorkspaces();
      
      let workspaces = [];
      if (response && response.data && response.data.workspaces) {
        workspaces = response.data.workspaces;
      } else if (response && response.workspaces) {
        workspaces = response.workspaces;
      } else if (Array.isArray(response)) {
        workspaces = response;
      }

      const defaultWorkspace = workspaces.find(ws => ws.user_default === true);
      
      if (!defaultWorkspace) {
        return false;
      }

      const workspaceResponse = await getWorkspaceById(defaultWorkspace.id);
      const fullWorkspaceData = workspaceResponse.data.workspace;
      
      const workspaceData = typeof fullWorkspaceData.ws_data === 'string' 
        ? JSON.parse(fullWorkspaceData.ws_data) 
        : fullWorkspaceData.ws_data;

      if (workspaceData.savedIndicators && workspaceData.savedIndicators.length > 0) {
        dispatch(setIndicators(workspaceData.savedIndicators));
      }
      
      if (workspaceData.savedLocations && workspaceData.savedLocations.length > 0) {
        const normalizedLocations = workspaceData.savedLocations.slice(1).map(loc => ({
          id: loc.id || loc.value || loc.lgd_code || null,
          name: loc.label || loc.lgd_name || loc.name || '',
          geo_level: loc.geo_level || loc.geo_level_code || null,
          properties: {
            id: loc.id || loc.value || loc.lgd_code || null,
            code: loc.value || loc.lgd_code || null,
            name: loc.label || loc.lgd_name || loc.name || '',
            geo_level_code: loc.geo_level || loc.geo_level_code || null,
          }
        }));
        dispatch(setMapIndicators(normalizedLocations));
      }

      dispatch(setWorkspaceLoaded({
        id: fullWorkspaceData.id,
        name: fullWorkspaceData.ws_name
      }));

      return true;
      
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    checkAuthStatus();

    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'userData' || e.key === null) {
        checkAuthStatus();
      }
    };

    const handleSessionTimeoutEvent = () => {
      setUser(null);
      setIsAuthenticated(false);
      dispatch(clearWorkspace());
      // Clear device ID on session timeout
      localStorage.removeItem('deviceId');
      // console.log('Session timeout - cleared device ID');
      // Session expired - redirect to home page instead of showing alert
      window.location.href = '/';
    };

    const handleSessionExtensionEvent = async (e) => {
      const { resolve, reject, endpoint, options } = e.detail;
      
      // Show the session extension modal with the resolve/reject callbacks
      setPendingRequest({ resolve, reject, endpoint, options });
      setShowSessionExtension(true);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionTimeout', handleSessionTimeoutEvent);
    window.addEventListener('showSessionExtension', handleSessionExtensionEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionTimeout', handleSessionTimeoutEvent);
      window.removeEventListener('showSessionExtension', handleSessionExtensionEvent);
    };
  }, [dispatch]);

  useEffect(() => {
    const autoLoadDefaultWorkspace = async () => {
      const state = store.getState();
      const selectedIndicators = state.workspace.selectedIndicators || [];
      const selectedMapFeatures = state.workspace.selectedMapFeatures || [];
      const isNewWorkspaceStarted = state.workspace.isNewWorkspaceStarted || false;
    
      if (isAuthenticated && user && !isWorkspaceLoaded && !isLoading && 
          selectedIndicators.length === 0 && selectedMapFeatures.length === 0 &&
          !isNewWorkspaceStarted) {
        
        try {
          await loadDefaultWorkspace();
        } catch (error) {
          // Handle error silently
        }
      }
    };

    const timeoutId = setTimeout(autoLoadDefaultWorkspace, 500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, user, isWorkspaceLoaded, isLoading]);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('deviceId');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData, token, refreshToken) => {

    let actualToken = token;
    if (!actualToken && userData && typeof userData === 'object') {
      actualToken = userData.token || userData.authToken || userData.accessToken;
    }
    
    if (actualToken) {
      localStorage.setItem('authToken', actualToken);
    }
    
    // Note: Refresh token is automatically set as HTTP-only cookie by backend
    // We don't need to store it in localStorage
    
    let cleanUserData = userData;
    if (userData && typeof userData === 'object') {
      const { token: userToken, authToken, accessToken, refreshToken: userRefreshToken, ...restUserData } = userData;
      cleanUserData = restUserData;
    }
    
    if (cleanUserData) {
      localStorage.setItem('userData', JSON.stringify(cleanUserData));
      setUser(cleanUserData);

    }
    
    setUser(cleanUserData);
    setIsAuthenticated(true);
  };

const handleSessionExtend = async () => {
  try {
    const refreshResult = await refreshToken();
    
    if (refreshResult && refreshResult.success) {
      if (refreshResult.user) {
        setUser(refreshResult.user);
      } else {
        const freshUserData = localStorage.getItem('userData');
        if (freshUserData) {
          const parsedUser = JSON.parse(freshUserData);
          setUser(parsedUser);
        }
      }
      
      setShowSessionExtension(false);
      
      if (pendingRequest && pendingRequest.resolve) {
        try {

          
          const { default: authModule } = await import('../../../store/auth');
          

          const newToken = localStorage.getItem('authToken');
          
          const isFormData = pendingRequest.options.isFormData;
          
          const retryConfig = {
            method: pendingRequest.options.method || 'GET',
            headers: {
              ...(!isFormData && { 'Content-Type': 'application/json' }),
              ...(newToken && { 'Authorization': `Bearer ${newToken}` }),
            },
            credentials: 'include',
          };
          
          if (pendingRequest.options.body) {
            retryConfig.body = pendingRequest.options.body;
          }
          
          const retryResponse = await fetch(`${BASE_URL}${pendingRequest.endpoint}`, retryConfig);
          const retryResult = await retryResponse.json();

          if (!retryResponse.ok) {
            throw new Error(retryResult.error || retryResult.message || 'Request failed after token refresh');
          }
          
          pendingRequest.resolve(retryResult);
          
        } catch (retryError) {
          pendingRequest.reject(retryError);
        }
      }
      
      setPendingRequest(null);
      
    } else {
      if (pendingRequest && pendingRequest.reject) {
        pendingRequest.reject(new Error('Session refresh failed'));
      }
      setPendingRequest(null);
      await logout();
    }
  } catch (error) {
    if (pendingRequest && pendingRequest.reject) {
      pendingRequest.reject(error);
    }
    setPendingRequest(null);
    await logout();
  }
};
  const handleSessionLogout = async () => {
    setShowSessionExtension(false);
    
    // Reject the pending request since user chose to logout
    if (pendingRequest && pendingRequest.reject) {
      pendingRequest.reject(new Error('User chose to logout'));
    }
    setPendingRequest(null);
    
    await logout();
  };


  // UPDATED logout function with device ID clearing
  const logout = async (navigateFunction) => {
    // Check if workspace was loaded/saved before clearing
    const state = store.getState();
    const isWorkspaceLoadedState = state.workspace.isWorkspaceLoaded;
    
    // Call backend logout API
    try {
      const { logout: logoutAPI } = await import('../../../store/auth');
      await logoutAPI();
    } catch (error) {
      console.error('Logout API error:', error);
    }
    
    // Clear auth data AND device ID
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('deviceId');  // Clear device ID on logout
    //console.log('Logout - cleared authToken, userData, and deviceId');
    
    setUser(null);
    setIsAuthenticated(false);
    
    // Only clear workspace if it was loaded/saved
    if (isWorkspaceLoadedState) {
      dispatch(clearWorkspace());
      // Also clear persisted workspace data from localStorage
      dispatch(clearPersistedWorkspace());
      // Manually clear the persisted workspace from localStorage
      localStorage.removeItem('persist:workspace');
    }
    
    // Use the passed navigate function if available
    if (navigateFunction && typeof navigateFunction === 'function') {
      navigateFunction('/');
    } else {
      // Fallback to window.location if no navigate function is provided
      window.location.href = '/';
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExtensionModal
        isVisible={showSessionExtension}
        onExtend={handleSessionExtend}
        onLogout={handleSessionLogout}
      />
    </AuthContext.Provider>
  );
};